import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import parse from "html-react-parser";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "../../Shared/Spinner";
import {
  APPLICATION_CATEGORY_LABELS,
  APPLICATION_CATEGORY_STYLES,
  ApplicationScoreBreakdown,
  GetCandidateApplicationBreakdown,
  QuestionScoreBreakdown,
} from "../../../api/roleApplicationScoring.api";

type Props = {
  roleId: string;
  candidateId: string;
  candidateName?: string;
  token: string;
  onClose: () => void;
};

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, ""));

/**
 * How one candidate's Role Q Grading was reached: every question on the role, what they answered,
 * and the points each answer earned. Fetched from the server, which runs the same calculation that
 * produced the grade in the list, so the two can't disagree.
 */
export default function ApplicationScoreBreakdownModal({
  roleId,
  candidateId,
  candidateName,
  token,
  onClose,
}: Props) {
  const [data, setData] = useState<ApplicationScoreBreakdown | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    GetCandidateApplicationBreakdown(roleId, candidateId, token)
      .then((res) => !cancelled && setData(res))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : "Failed to load"));
    return () => {
      cancelled = true;
    };
  }, [roleId, candidateId, token]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const renderQuestion = (q: QuestionScoreBreakdown) => {
    const muted = q.status !== "Scored";
    return (
      <div
        key={q.questionId}
        className={`rounded-lg border border-gray-200 dark:border-gray-700 p-3 ${q.isFollowUp ? "ml-6" : ""} ${muted ? "opacity-70" : ""}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {q.isFollowUp && <span className="text-gray-400 mr-1">↳</span>}
            {parse(q.questionText)}
          </div>
          <div className="shrink-0 text-right">
            {q.status === "Scored" ? (
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {fmt(q.pointsEarned)} / {fmt(q.pointsPossible)}
              </span>
            ) : (
              <span className="text-xs text-gray-500 dark:text-gray-400 italic">
                {q.status === "NotShown" ? "Not shown - not counted" : "Not scored"}
              </span>
            )}
          </div>
        </div>

        <div className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          {q.inputType === "text"
            ? "Text answer - full points if answered, 0 if empty"
            : q.inputType === "number"
              ? "Number answer"
              : q.isMultiple
                ? "Multiple choice - points add up"
                : "Single choice"}
        </div>

        <div className="mt-2">
          {q.answers.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">
              {q.status === "NotShown" ? "Not asked" : "No answer"}
            </p>
          ) : (
            <ul className="space-y-1">
              {q.answers.map((a, i) => (
                <li key={i} className="flex items-start justify-between gap-4 text-xs">
                  <span className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                    {a.text}
                  </span>
                  <span className="shrink-0 text-gray-500 dark:text-gray-400">
                    {a.weight != null ? `${fmt(a.weight)} pts` : "-"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  // Portalled to <body>: the role page's content area gets a CSS transform while its sidebar is
  // open, which makes `position: fixed` relative to that area instead of the screen. The
  // stopPropagation still matters - React events bubble through portals to the table row.
  return createPortal(
    <div className="fixed inset-0 z-[60] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
        <div className="relative w-full max-w-2xl bg-white dark:bg-darkbglight rounded-lg shadow-xl text-left whitespace-normal">
          <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-300">
                QGrade
              </h3>
              {candidateName && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{candidateName}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {error ? (
            <p className="p-6 text-sm text-red-600 dark:text-red-400">{error}</p>
          ) : !data ? (
            <div className="py-12">
              <Spinner height="h-8" width="w-8" />
            </div>
          ) : (
            <>
              <div className="px-6 pt-4 flex flex-wrap items-center gap-3">
                <span
                  className={`rounded-full px-3 py-1 text-sm font-semibold ${APPLICATION_CATEGORY_STYLES[data.category]}`}
                >
                  {data.score != null ? `${fmt(data.score)}% · ` : ""}
                  {APPLICATION_CATEGORY_LABELS[data.category]}
                </span>
                {data.pointsPossible > 0 && (
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {fmt(data.pointsEarned)} of {fmt(data.pointsPossible)} points
                  </span>
                )}
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Relevant from {fmt(data.relevantThreshold)}% · Good from {fmt(data.goodThreshold)}%
                </span>
              </div>

              <div className="p-6 max-h-[60vh] overflow-y-auto space-y-3">
                {data.questions.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    This role has no application questions.
                  </p>
                ) : (
                  data.questions.map(renderQuestion)
                )}
              </div>
            </>
          )}

          <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-md transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
